import * as React from 'react';
import { useEffect, useState } from 'react';
import { languages } from 'prismjs/components/prism-core';
import Loader from '../../../../core/components/Loader.tsx';
import classnames from '../../../../core/utils/classnames.tsx';
import DataViewer from './DataViewer.tsx';

import 'prismjs/components/prism-markup';

const INDENT = '    ';

// E-invoices (XRechnung, ZUGFeRD) frequently arrive as a single line, which is unreadable in a review pane.
const formatXml = (xml: string): string => {
    const lines = xml.replace(/>\s*</g, '>\n<').split('\n');

    let depth = 0;
    return lines
        .map((rawLine) => {
            const line = rawLine.trim();
            if (!line) return undefined;

            const isClosingTag = line.startsWith('</');
            const isDeclarationOrComment = /^<[!?]/.test(line);
            const isSelfClosing = line.endsWith('/>');
            const isCompleteElement = !isClosingTag && line.includes('</');

            if (isClosingTag) depth = Math.max(depth - 1, 0);
            const indented = INDENT.repeat(depth) + line;
            if (!isClosingTag && !isDeclarationOrComment && !isSelfClosing && !isCompleteElement) depth += 1;

            return indented;
        })
        .filter((line): line is string => line !== undefined)
        .join('\n');
};

const XmlPreview = ({ data, className = '', ...props }: any) => {
    const [xml, setXml] = useState<string | undefined>(undefined);
    const [unreadable, setUnreadable] = useState(false);

    useEffect(() => {
        let cancelled = false;
        setXml(undefined);
        setUnreadable(false);

        fetch(data.url)
            .then((response) => {
                if (!response.ok) throw new Error(`Failed to load XML: ${response.status}`);
                return response.text();
            })
            .then((text) => {
                if (!cancelled) setXml(formatXml(text));
            })
            .catch(() => {
                // The storage URL may not allow cross-origin reads; let the browser render the file instead.
                if (!cancelled) setUnreadable(true);
            });

        return () => {
            cancelled = true;
        };
    }, [data.url]);

    if (unreadable) {
        return <iframe src={data.url} className={classnames('block w-full h-[90vh]', className)} data-type="xml" />;
    }

    if (xml === undefined) {
        return <Loader className="p-8 m-0 max-w-[62rem]" />;
    }

    return (
        <DataViewer
            value={xml}
            language={languages.markup}
            className={classnames('block w-full bg-surface-weak', className)}
            data-type="xml"
            {...props}
        />
    );
};

export default XmlPreview;
